import { useEffect, useState, useRef } from 'react';
import { axiosPrivate } from '../../../api/axios';
import axios from '../../../api/axios';
import useAuth from '../../../hooks/useAuth';

// Context
import { useGlobalAdminSettings } from '../../../context/AdminSettingsProvider';
import { useGlobalSuperAdminSettings } from '../../../context/SuperAdminSettingsProvider';

// Components
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import Error from '../Error';

// Translation
import { useTranslation } from 'react-i18next';

// Styling
import '../../../css/HomeAdmin.css';
import '../../../css/AdminSubscription.css';

// Stripe
import { loadStripe } from '@stripe/stripe-js';

export default function AdminSubscription({ isNavOpen }) {
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  const { t, i18n } = useTranslation();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { auth } = useAuth();
  const isAdmin = auth?.roles?.includes('Admin');

  const [school, setSchool] = useState(null);
  const schoolId = school?.school_id;
  const [loading, setLoading] = useState(true);
  const [adminSubscription, setAdminSubscription] = useState(null);

  // console.log('adminSubscription', adminSubscription)

  const [allAdminSubscriptions, setAllAdminSubscriptions] = useState(null);
  // console.log('allAdminSubscriptions', allAdminSubscriptions)
  const [customRoles, setCustomRoles] = useState(0);
  const [registeredUsers, setRegisteredUsers] = useState(0);
  const [adminSubscriptionOverrides, setAdminSubscriptionOverrides] = useState([]);

  // console.log('new fetchAdminSubscriptionOverrides result', adminSubscriptionOverrides)

  // Helper to get override for a specific plan
  const getOverrideForPlan = (planId) => {
    if (!adminSubscriptionOverrides) return null;
    return adminSubscriptionOverrides.find(o => o.override_for_plan_id === planId) || null;
  };


  // Correct override lookup (must be above storage calculations)
  const currentPlanOverride = getOverrideForPlan(adminSubscription?.id);

  //  console.log('adminSubscriptionOverrides', adminSubscriptionOverrides)
  const [loadingButtonId, setLoadingButtonId] = useState(null);

  // Which plan is being confirmed in the modal
  const [confirmChange, setConfirmChange] = useState(null);

  const { refreshSuperAdminSettings } = useGlobalSuperAdminSettings();
  const { refreshAdminSettings } = useGlobalAdminSettings();

  const isSchoolAdmin = school?.admin_email === auth.email;

  const waitForSubscriptionSync = async (schoolId, maxRetries = 5) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const sub = await fetchAdminSubscription(schoolId);

        if (sub?.stripe_subscription_id) {
          return true; // ✅ synced
        }
      } catch (e) {
        // swallow transient errors
      }

      await new Promise(r => setTimeout(r, 1000));
    }

    return false; // ⏳ not synced yet (NOT an error)
  };


  const STRIPE_PRICE_IDS = {
    test: {
      starter: process.env.REACT_APP_STRIPE_PRICE_TEST_STARTER,
      essential: process.env.REACT_APP_STRIPE_PRICE_TEST_ESSENTIAL,
      pro: process.env.REACT_APP_STRIPE_PRICE_TEST_PRO,
    },
    live: {
      starter: process.env.REACT_APP_STRIPE_PRICE_LIVE_STARTER,
      essential: process.env.REACT_APP_STRIPE_PRICE_LIVE_ESSENTIAL,
      pro: process.env.REACT_APP_STRIPE_PRICE_LIVE_PRO,
    },
  };

  const ENV =
    process.env.NODE_ENV === 'production' ? 'live' : 'test';

  const getStripePriceId = (planKey) => {
    const priceId = STRIPE_PRICE_IDS[ENV]?.[planKey];

    if (!priceId) {
      throw new Error(
        `Stripe price ID not configured for plan "${planKey}" in "${ENV}" mode`
      );
    }

    return priceId;
  };



  // Cancel / Reactivate messages
  const [subStatus, setSubStatus] = useState({
    cancel: null,
    reactivate: null,
  });

  // Global banner (for redirect results, errors, etc.)
  const [banner, setBanner] = useState(null); // { type: 'success' | 'error', text: string }
  const showBanner = (type, text) => setBanner({ type, text });
  const hideBanner = () => setBanner(null);


  const resetStatuses = () =>
    setSubStatus({
      cancel: null,
      reactivate: null,
    });


  // Storage usage
  const [storage, setStorage] = useState({
    home: 0,
    profile: 0,
    audio: 0,
    video: 0,
    total: 0,
  });

  const usedStorageMB = storage.total / (1024 * 1024);



  // Correct storage limit logic
  const effectiveStorageLimitMB =
    currentPlanOverride?.storage_mb != null
      ? Number(currentPlanOverride.storage_mb)
      : Number(adminSubscription?.storage_mb ?? 0);

  const storageUsagePercent =
    effectiveStorageLimitMB > 0
      ? (usedStorageMB / effectiveStorageLimitMB) * 100
      : 0;

  const storageBarColor = storageUsagePercent >= 90 ? '#e74c3c' : '#2ecc71';



  const newPlanOverride = getOverrideForPlan(confirmChange?.id);

  const currentHasOverride = !!currentPlanOverride;
  const newHasOverride = !!newPlanOverride;




  // console.log("---- STORAGE DEBUG ----");
  // console.log("Base storage:", adminSubscription?.storage_mb);
  // console.log("Override storage:", adminSubscriptionOverrides?.storage_mb);
  // console.log("override_for_plan_id:", adminSubscriptionOverrides?.override_for_plan_id);
  // console.log("current plan id:", adminSubscription?.id);
  // console.log("isOverrideCurrentPlanGlobal:", isOverrideCurrentPlanGlobal);
  // console.log("effectiveStorageLimitMB:", effectiveStorageLimitMB);
  // console.log("usedStorageMB:", usedStorageMB);
  // console.log("storageUsagePercent:", storageUsagePercent);

  const isDifferent = (a, b) => Number(a) !== Number(b);


  const [usedLevels, setUsedLevels] = useState(0);
  const [showStorageDetailsToggle, setShowStorageDetailsToggle] = useState(false);
  const toggleStorageDetails = () =>
    setShowStorageDetailsToggle((prev) => !prev);

  const [customInfoOpen, setCustomInfoOpen] = useState({});

  const redirectingRef = useRef(false);

  // -------- Fetch helpers --------
  const fetchSchoolData = async () => {
    if (!backendUrl) return null;
    setLoading(true);
    try {
      const response = await axiosPrivate.get('/school/admin');
      const schoolData = response.data[0] || null;
      setSchool(schoolData);
      return schoolData; // ✅ ADD THIS
    } catch (err) {
      console.error('Error fetching school data:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };


  const fetchCustomRoles = async () => {
    try {
      const res = await axios.get('/custom-roles-public');
      setCustomRoles(res.data);
      setUsedLevels(Array.isArray(res.data) ? res.data.length : 0);
    } catch (err) {
      console.error('Failed to fetch custom roles:', err);
    }
  };

  const fetchAdminSubscription = async (schoolId) => {
    if (!schoolId) return;
    try {
      const res = await axiosPrivate.get(
        `/admin/subscription?school_id=${schoolId}`
      );
      setAdminSubscription(res.data);
    } catch (err) {
      console.error('Failed to fetch admin subscription:', err);
    }
  };

  const fetchAdminSubscriptionOverrides = async (schoolId) => {
    if (!schoolId) return;
    try {
      const res = await axiosPrivate.get(`/admin/subscription/override?school_id=${schoolId}`);
      setAdminSubscriptionOverrides(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setAdminSubscriptionOverrides([]);
    }
  };




  const fetchRegisteredUsers = async () => {
    try {
      const res = await axiosPrivate.get(
        `/admin/subscription/registered-users`
      );
      setRegisteredUsers(res.data.userCount);
    } catch (err) {
      console.error('Failed to fetch registered users:', err);
    }
  };



  const fetchStorage = async () => {
    try {
      const [home, profile, audio, video] = await Promise.all([
        axiosPrivate.get('/storage/home'),
        axiosPrivate.get('/storage/profile_pictures'),
        axiosPrivate.get('/storage/audios'),
        axiosPrivate.get('/storage/videos'),
      ]);
      const data = {
        home: home.data.size || 0,
        profile: profile.data.size || 0,
        audio: audio.data.size || 0,
        video: video.data.size || 0,
      };
      setStorage({
        ...data,
        total: Object.values(data).reduce((a, b) => a + b, 0),
      });
    } catch (err) {
      console.error('Failed to fetch storage:', err);
    }
  };

  const fetchAllAdminSubscriptions = async () => {
    try {
      const res = await axiosPrivate.get(`/admin/subscription/all`);
      setAllAdminSubscriptions(res.data);
    } catch (err) {
      console.error('Failed to fetch all subscriptions:', err);
    }
  };

  // -------- Handle Stripe redirect confirmation --------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('cancelled');

    if (!sessionId && !cancelled) return;

    const confirm = async () => {
      try {
        if (cancelled) {
          showBanner('error', t('adminSubscription.paymentCancelled'));
          return;
        }

        // noop endpoint
        await axiosPrivate.post('/api/stripe/confirm-subscription', {
          session_id: sessionId,
        });

        // 🔑 fresh school data
        const freshSchool = await fetchSchoolData();

        let synced = false;

        // 🔁 wait for webhook (soft wait, never throws)
        if (freshSchool?.school_id) {
          synced = await waitForSubscriptionSync(freshSchool.school_id);
        }


        await refreshSuperAdminSettings();
        await refreshAdminSettings();

        // ✅ SUCCESS EVEN IF sync WAS SLOW
        showBanner(
          'success',
          synced
            ? t('adminSubscription.subscriptionSuccess')
            : t('adminSubscription.subscriptionSuccessDelayed')
        );

      } catch (err) {
        console.error('Stripe confirmation error:', err);

        const status = err?.response?.status;

        if (status === 409 || status === 422) {
          showBanner('error', t('adminSubscription.overLimitError'));
        } else {
          showBanner('error', t('adminSubscription.confirmationFailed'));
        }
      } finally {
        window.history.replaceState(
          {},
          document.title,
          '/admin/adminsubscription'
        );
      }
    };

    confirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Auto-dismiss banner when clicking outside
  useEffect(() => {
    if (!banner) return;
    const onClick = (e) => {
      if (e.target.closest('.subscription-banner')) return;
      hideBanner();
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [banner]);

  // Initial loads
  useEffect(() => {
    fetchSchoolData();
    fetchCustomRoles();
    fetchAllAdminSubscriptions();
    fetchRegisteredUsers();
    fetchStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl]);

  // Refresh dependent data when we know the school
  useEffect(() => {
    if (schoolId) {
      fetchAdminSubscription(schoolId);
      fetchAdminSubscriptionOverrides(schoolId);
    }
  }, [schoolId]);

  // -------- Helpers --------
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatStorageSize = (mb) => {
    if (mb == null || isNaN(mb)) return '0 MB';  // <-- prevents crash

    const num = Number(mb);

    return num >= 1024
      ? `${(num / 1024).toFixed(1)} GB`
      : `${num.toFixed(1)} MB`;
  };



  // Core subscription switch handler (no eligibility checks)
  const handleSubscriptionClick = async (sub, opts = {}) => {
    try {
      const check = await axiosPrivate.post('/school/subscribe/check', {
        subscription_id: sub.id
      });


      if (check.data?.ok === false) {
        showBanner('error', t('adminSubscription.overLimitError'));
        return;
      }
    } catch (err) {
      // PRE-CHECK FAILS BECAUSE SERVER IS DOWN / NETWORK ERROR
      if (!err.response) {
        showBanner('error', t('adminSubscription.subscriptionErrorNetwork'));
      } else if (err.response.status === 400) {
        showBanner('error', t('adminSubscription.overLimitError'));
      } else {
        showBanner('error', t('adminSubscription.subscriptionError2'));
      }
      return;
    }


    const suppressLoading = !!opts.suppressLoading;
    if (!suppressLoading) setLoadingButtonId(sub.id);

    const planKey = getSubscriptionKey(sub.subscription_name);
    const priceId = getStripePriceId(planKey);


    try {
      const res = await axiosPrivate.post('/api/stripe/update-subscription', {
        newPriceId: priceId,
      });

      if (res.status === 200) {
        const msg = t('adminSubscription.subscriptionUpdated');
        showBanner('success', msg);
        resetStatuses();
        await fetchSchoolData();
        await fetchAdminSubscription(school?.school_id);
        await refreshSuperAdminSettings();
        await refreshAdminSettings();
      }
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.sessionUrl) {
        redirectingRef.current = true;   // <-- MARK redirect
        window.location.href = err.response.data.sessionUrl;
        return;

      }

      // ---------- NETWORK OFFLINE / STRIPE UNREACHABLE ----------
      if (!err.response) {
        showBanner('error', t('adminSubscription.subscriptionErrorNetwork'));
      }
      // ---------- LIMIT VIOLATION ----------
      else if (err.response.status === 400) {
        showBanner('error', t('adminSubscription.overLimitError'));
      }
      // ---------- SERVER ERROR (500+) ----------
      else if (err.response.status >= 500) {
        showBanner('error', t('adminSubscription.subscriptionError2'));
      }
      // ---------- EVERYTHING ELSE ----------
      else {
        showBanner('error', t('adminSubscription.subscriptionError2'));
      }

      console.error('Subscription update error:', err);

    } finally {
      if (!redirectingRef.current && !suppressLoading) {
        setLoadingButtonId(null);
      }
    }
  };


  // --- Helpers ---
  const getSubscriptionKey = (name) => {
    if (!name) return '';
    const parts = name.split('.');
    if (parts.length >= 3) return parts[1]; // subscription.starter.name -> starter
    return name;
  };

  const getAddonKey = (name) => {
    if (!name) return '';
    const parts = name.trim().split('.');
    if (
      parts.length >= 3 &&
      parts[0] === 'subscription' &&
      parts[1] === 'addons'
    ) {
      return parts[2]; // subscription.addons.socialPosts -> socialPosts
    }
    return name.trim();
  };




  const CustomInfoBadge = ({ id }) => (
    <span
      className="custom-info-badge"
      onClick={(e) => {
        e.stopPropagation();
        setCustomInfoOpen(prev => ({
          ...prev,
          [id]: !prev[id]
        }));
      }}
    >
      {t('adminSubscription.customLabel')}
    </span>
  );


  const CustomInfoBox = ({ id }) =>
    customInfoOpen[id] ? (
      <div className="custom-info-box">
        {t('adminSubscription.customInfoMessage')}
      </div>
    ) : null;


  const isCustomValue = (base, override) =>
    override != null && base != null && override !== base;


  // -------- JSX --------
  return (
    <div className={isNavOpen ? 'body-squeezed' : 'body'}>

      {loading ? (
        <LoadingSpinner />
      ) : school ? (
        <div className="homeAdmin_container force-center" >
          {/* Global banner */}
          {banner && (
            <div className={`subscription-banner ${banner.type}`}>
              <span>{banner.text}</span>
              <button className="close-banner" onClick={hideBanner}>
                ✕
              </button>
            </div>
          )}



          {!confirmChange &&
            <div className="subscription-cards-container">



              {adminSubscription && allAdminSubscriptions && (
                <div className="subscription-cards-container">



                  {allAdminSubscriptions.map((sub) => {
                    const isCurrent = sub.id === adminSubscription?.id;

                    // ▼ NEW (per-plan override state for this card)


                    // Collect non-empty addons dynamically
                    const addons = [];
                    for (let i = 1; i <= 10; i++) {
                      const addon = sub[`subscription_addon${i}`];
                      if (addon && addon.trim() !== '') {
                        addons.push(addon);
                      }
                    }

                    const override = getOverrideForPlan(sub.id);
                    const overrideActive = !!override && school?.subscription_id === sub.id;
                    const overridePreview = !!override && !overrideActive;

                    const getSubscriptionKey = (name) => {
                      if (!name) return '';
                      const parts = name.split('.');
                      if (parts.length >= 3) return parts[1]; // subscription.starter.name -> starter
                      return name;
                    };


                    return (
                      <div
                        key={sub.id}
                        className={`subscription-card ${isCurrent ? 'current-subscription' : ''
                          }`}
                      >
                        {isCurrent && (
                          <div className="current-subscription-banner">
                            {t('adminSubscription.currentSubscription')}
                          </div>
                        )}

                        <h3>
                          {t(
                            `adminSubscription.subscription.${getSubscriptionKey(
                              sub.subscription_name
                            )}.name`
                          )}
                        </h3>

                        <p className="subscription-purpose">
                          {t(
                            `adminSubscription.subscription.${getSubscriptionKey(
                              sub.subscription_purpose
                            )}.purpose`
                          )}
                        </p>

                        {/* PRICE */}
                        <div className="subscription-price">

                          {/* ACTIVE override — values differ */}
                          {overrideActive && override && isDifferent(sub.price, override.price) && (
                            <>
                              <div className="overridden-value">
                                CAD ${sub.price} / {t('adminSubscription.month')}
                              </div>

                              <div className="subscription-price-new">
                                CAD ${override.price} / {t('adminSubscription.month')}
                                <CustomInfoBadge id={`price-${sub.id}`} />
                                <CustomInfoBox id={`price-${sub.id}`} />
                              </div>
                            </>
                          )}

                          {/* PREVIEW override — values differ */}
                          {overridePreview && override && isDifferent(sub.price, override.price) && (
                            <>
                              <div className="overridden-value">
                                <s>CAD ${sub.price}</s>
                              </div>
                              <div className="override-preview-value">
                                CAD ${override.price}
                                <CustomInfoBadge id={`price-${sub.id}`} />
                                <CustomInfoBox id={`price-${sub.id}`} />
                              </div>
                            </>
                          )}

                          {/* NORMAL (also shown when overrideActive but values are identical) */}
                          {(!overrideActive && !overridePreview) ||
                            (override && !isDifferent(sub.price, override.price)) ? (
                            <div>
                              CAD ${sub.price} / {t('adminSubscription.month')}
                            </div>
                          ) : null}
                        </div>



                        <ul className="subscription-details">
                          {/* Levels */}
                          <li>
                            <strong>{t('adminSubscription.levels')}</strong>{' '}

                            {/* ACTIVE override */}
                            {overrideActive && override && isDifferent(sub.number_of_levels, override.number_of_levels) && (
                              <>
                                {usedLevels} / {override.number_of_levels}{' '}
                                <span className="overridden-value">
                                  <s>{sub.number_of_levels}</s>
                                </span>
                                <CustomInfoBadge id={`levels-${sub.id}`} />
                                <CustomInfoBox id={`levels-${sub.id}`} />
                              </>
                            )}

                            {/* PREVIEW override */}
                            {overridePreview && isDifferent(sub.number_of_levels, override.number_of_levels) && (
                              <>
                                {override.number_of_levels}{' '}
                                <span className="overridden-value">
                                  <s>{sub.number_of_levels}</s>
                                </span>
                                <CustomInfoBadge id={`levels-${sub.id}`} />
                                <CustomInfoBox id={`levels-${sub.id}`} />
                              </>
                            )}

                            {/* NORMAL */}
                            {(!overrideActive && !overridePreview) ||
                              (override && !isDifferent(sub.number_of_levels, override.number_of_levels)) ? (
                              <>
                                {isCurrent ? (
                                  <>
                                    {usedLevels} / {sub.number_of_levels}
                                  </>
                                ) : (
                                  <>
                                    {sub.number_of_levels}
                                  </>
                                )}
                              </>
                            ) : null}
                          </li>




                          {/* USERS */}
                          <li>
                            <strong>{t('adminSubscription.users')}</strong>{' '}

                            {/* ACTIVE override */}
                            {overrideActive && override && isDifferent(sub.number_of_users, override.number_of_users) && (
                              <>
                                {registeredUsers} / {override.number_of_users}{' '}
                                <span className="overridden-value">
                                  <s>{sub.number_of_users}</s>
                                </span>
                                <CustomInfoBadge id={`users-${sub.id}`} />
                                <CustomInfoBox id={`users-${sub.id}`} />
                              </>
                            )}

                            {/* PREVIEW override */}
                            {overridePreview && isDifferent(sub.number_of_users, override.number_of_users) && (
                              <>
                                {override.number_of_users}{' '}
                                <span className="overridden-value">
                                  <s>{sub.number_of_users}</s>
                                </span>
                                <CustomInfoBadge id={`users-${sub.id}`} />
                                <CustomInfoBox id={`users-${sub.id}`} />
                              </>
                            )}

                            {/* NORMAL (also used when overrideActive but equal values) */}
                            {(!overrideActive && !overridePreview) ||
                              (override && !isDifferent(sub.number_of_users, override.number_of_users)) ? (
                              <>
                                {isCurrent ? (
                                  <>
                                    {registeredUsers} / {sub.number_of_users}
                                  </>
                                ) : (
                                  <>{sub.number_of_users}</>
                                )}
                              </>
                            ) : null}
                          </li>





                          {/* Storage */}
                          {isCurrent ? (
                            <li>
                              <strong>{t('adminSubscription.storage')}</strong>{' '}
                              <span
                                style={{ cursor: 'pointer', marginLeft: '8px' }}
                                onClick={toggleStorageDetails}
                              >
                                {showStorageDetailsToggle ? '−' : '+'}
                              </span>{' '}
                              {overrideActive && override && isCustomValue(sub.storage_mb, override.storage_mb) ? (
                                <>
                                  {formatStorageSize(usedStorageMB)} / {formatStorageSize(override?.storage_mb ?? sub.storage_mb)}{' '}
                                  <span
                                    className="overridden-value"
                                    style={{
                                      color: 'red',
                                      textDecoration: 'line-through',
                                      marginLeft: '8px',
                                    }}
                                  >
                                    {formatStorageSize(sub.storage_mb)}
                                  </span>
                                  <CustomInfoBadge id={`storage-${sub.id}`} />
                                  <CustomInfoBox id={`storage-${sub.id}`} />
                                </>
                              ) : (
                                <>
                                  {formatStorageSize(usedStorageMB)} / {formatStorageSize(sub.storage_mb)}
                                </>
                              )}

                              {/* Storage bar */}
                              <div className="storage-bar-wrapper">
                                <div className="storage-bar-row">
                                  <div className="storage-bar-container">
                                    <div
                                      className="storage-bar-fill"
                                      style={{ width: `${storageUsagePercent}%`, backgroundColor: storageBarColor }}
                                      title={`${storageUsagePercent.toFixed(1)}% used`}
                                    ></div>
                                  </div>
                                  <div className="storage-bar-percent">
                                    {storageUsagePercent.toFixed(1)}%
                                  </div>
                                </div>
                              </div>

                              {/* Storage details */}
                              {showStorageDetailsToggle && (
                                <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                                  <li>{t('adminSubscription.home')} {formatBytes(storage.home)}</li>
                                  <li>{t('adminSubscription.profilePictures')} {formatBytes(storage.profile)}</li>
                                  <li>{t('adminSubscription.audio')} {formatBytes(storage.audio)}</li>
                                  <li>{t('adminSubscription.video')} {formatBytes(storage.video)}</li>
                                </ul>
                              )}
                            </li>

                          ) : (
                            <li>
                              <strong>{t('adminSubscription.storage')}</strong>{' '}

                              {/* PREVIEW override */}
                              {overridePreview && override && isDifferent(sub.storage_mb, override.storage_mb) ? (
                                <>
                                  {formatStorageSize(override?.storage_mb ?? sub.storage_mb)}{' '}
                                  <span className="overridden-value">
                                    <s>{formatStorageSize(sub.storage_mb)}</s>
                                  </span>
                                  <CustomInfoBadge id={`storage-prev-${sub.id}`} />
                                  <CustomInfoBox id={`storage-prev-${sub.id}`} />
                                </>
                              ) : (
                                <>
                                  {sub.storage_mb >= 1024
                                    ? `${(sub.storage_mb / 1024).toFixed(1)} GB`
                                    : `${sub.storage_mb} MB`}
                                </>
                              )}
                            </li>


                          )}


                          {/* ADD-ONS */}
                          <li>
                            <ul className="addons-list">
                              {Array.from({ length: 10 }).map((_, index) => {
                                const rawBase = sub[`subscription_addon${index + 1}`];
                                const rawOverride = override?.[`subscription_addon${index + 1}`];

                                const baseKey = rawBase ? getAddonKey(rawBase) : null;
                                const overrideKey = rawOverride ? getAddonKey(rawOverride) : null;

                                const hasBase = !!baseKey;
                                const hasOverride = !!overrideKey;

                                // If nothing exists, skip this slot completely.
                                if (!hasBase && !hasOverride) return null;

                                // Is this slot truly “custom” for this plan?
                                const customActive = overrideActive && hasOverride && (!hasBase || overrideKey !== baseKey);
                                const customPreview = overridePreview && hasOverride && (!hasBase || overrideKey !== baseKey);

                                // ✅ CASE 1 — ACTIVE override
                                if (overrideActive && hasOverride) {
                                  const sameAddon = hasBase && baseKey === overrideKey;

                                  // 1A) No base OR base === override → show only the override (NO badge if same)
                                  if (!hasBase || sameAddon) {
                                    return (
                                      <li key={index} className="addon-item">
                                        {t(`adminSubscription.subscription.addons.${overrideKey}`)}
                                        {customActive && (
                                          <>
                                            <CustomInfoBadge id={`addon-active-${sub.id}-${index}`} />
                                            <CustomInfoBox id={`addon-active-${sub.id}-${index}`} />
                                          </>
                                        )}
                                      </li>
                                    );
                                  }

                                  // 1B) Base and override differ → crossed base + new override (+ badge)
                                  return (
                                    <li key={index} className="addon-item">
                                      <span className="overridden-value">
                                        <s>{t(`adminSubscription.subscription.addons.${baseKey}`)}</s>
                                      </span>{' '}
                                      <span>{t(`adminSubscription.subscription.addons.${overrideKey}`)}</span>
                                      {customActive && (
                                        <>
                                          <CustomInfoBadge id={`addon-active-diff-${sub.id}-${index}`} />
                                          <CustomInfoBox id={`addon-active-diff-${sub.id}-${index}`} />
                                        </>
                                      )}
                                    </li>
                                  );
                                }

                                // ✅ CASE 2 — PREVIEW override
                                if (overridePreview && hasOverride) {
                                  return (
                                    <li key={index} className="addon-item">
                                      {t(`adminSubscription.subscription.addons.${overrideKey}`)}
                                      {customPreview && (
                                        <>
                                          <CustomInfoBadge id={`addon-prev-${sub.id}-${index}`} />
                                          <CustomInfoBox id={`addon-prev-${sub.id}-${index}`} />
                                        </>
                                      )}
                                    </li>
                                  );
                                }

                                // ✅ CASE 3 — NORMAL
                                if (hasBase) {
                                  return (
                                    <li key={index} className="addon-item">
                                      {t(`adminSubscription.subscription.addons.${baseKey}`)}
                                    </li>
                                  );
                                }

                                return null;
                              })}

                            </ul>
                          </li>





                        </ul>



                        {/* Current plan → show cancel / reactivate */}
                        {isCurrent ? (
                          <>
                            {school?.subscription_status === 'cancel_pending' ? (
                              <div className="subscription-message pending">
                                {t('adminSubscription.cancelPending', {
                                  date: new Date(
                                    school.cancel_at
                                  ).toLocaleDateString(i18n.language, {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  }),
                                })}
                                {loadingButtonId === 'reactivate' ? (
                                  <LoadingSpinner />
                                ) : (
                                  <div
                                    title={!isSchoolAdmin ? t('adminSubscription.accessibleOnlyToPrincipal') : ''}
                                    style={{ display: 'inline-block' }}
                                  >
                                    <button
                                      className="subscribe-button"
                                      disabled={!isSchoolAdmin || loadingButtonId !== null}
                                      onClick={async () => {
                                        if (!isSchoolAdmin || loadingButtonId !== null) return;

                                        try {
                                          // 1️⃣ Pre-check → blocks downgrade if over limits
                                          const checkRes = await axiosPrivate.post('/school/subscribe/check', {
                                            subscription_id: sub.id
                                          });

                                          if (checkRes.data?.ok === false) {
                                            let detailsText = '';

                                            if (Array.isArray(checkRes.data.details)) {
                                              detailsText = checkRes.data.details.join(', ');
                                            }

                                            showBanner('error', `${t('adminSubscription.overLimitError')} ${detailsText}`);
                                            return;
                                          }


                                        } catch (err) {
                                          if (err?.response?.status === 400) {
                                            showBanner('error', t('adminSubscription.overLimitError'));
                                            return; // ⛔ STOP HERE
                                          }

                                          console.error('Eligibility check failed:', err);
                                          showBanner('error', t('adminSubscription.overLimitError'));
                                          return;
                                        }

                                        // 4️⃣ Allowed → proceed normally
                                        if (school?.stripe_subscription_id) {
                                          setConfirmChange(sub);
                                        } else {
                                          handleSubscriptionClick(sub);
                                        }
                                      }}
                                    >
                                      {loadingButtonId === sub.id ? (
                                        <LoadingSpinner />
                                      ) : (
                                        t('adminSubscription.subscribe')
                                      )}
                                    </button>
                                  </div>


                                )}

                                {subStatus.reactivate?.type === 'error' && (
                                  <div className="subscription-message error">
                                    {subStatus.reactivate.text}
                                  </div>
                                )}
                                {subStatus.reactivate?.type === 'success' && (
                                  <div className="subscription-message success">
                                    {subStatus.reactivate.text}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                {school?.subscription_id !== 1 && (
                                  <>
                                    {loadingButtonId === 'cancel' ? (
                                      <LoadingSpinner />
                                    ) : (
                                      <button
                                        className="button-red"
                                        disabled={!isSchoolAdmin}
                                        onClick={async () => {
                                          resetStatuses();
                                          setLoadingButtonId('cancel');
                                          try {
                                            setSubStatus((prev) => ({
                                              ...prev,
                                              cancel: {
                                                type: 'loading',
                                                text: t(
                                                  'adminSubscription.processing',
                                                  'Processing.'
                                                ),
                                              },
                                            }));

                                            const res =
                                              await axiosPrivate.post(
                                                '/api/stripe/cancel-subscription',
                                                {
                                                  customerEmail: auth?.email,
                                                }
                                              );

                                            const cancelDate = res.data.cancel_at
                                              ? new Date(
                                                res.data.cancel_at
                                              ).toLocaleDateString(
                                                i18n.language,
                                                {
                                                  year: 'numeric',
                                                  month: 'long',
                                                  day: 'numeric',
                                                }
                                              )
                                              : null;

                                            await fetchSchoolData();
                                            await fetchAdminSubscription(
                                              school?.school_id
                                            );
                                            await refreshSuperAdminSettings();
                                            await refreshAdminSettings();

                                            const msg = cancelDate
                                              ? t(
                                                'adminSubscription.cancelSuccessWithDate',
                                                { date: cancelDate }
                                              )
                                              : t(
                                                'adminSubscription.cancelSuccessImmediate'
                                              );

                                            setSubStatus({
                                              cancel: {
                                                type: 'success',
                                                text: msg,
                                              },
                                              reactivate: null,
                                            });
                                            showBanner('success', msg);
                                          } catch (err) {
                                            console.log(
                                              err?.response?.data?.error
                                            );
                                            if (
                                              err?.response?.data?.error ===
                                              'Unauthorized admin email'
                                            ) {
                                              setSubStatus((prev) => ({
                                                ...prev,
                                                cancel: {
                                                  type: 'error',
                                                  text: t(
                                                    'adminSubscription.emailNotAuthorized'
                                                  ),
                                                },
                                              }));
                                            } else {
                                              console.error(
                                                'Cancel subscription error:',
                                                err
                                              );
                                              setSubStatus((prev) => ({
                                                ...prev,
                                                cancel: {
                                                  type: 'error',
                                                  text: t(
                                                    'adminSubscription.subscriptionError2'
                                                  ),
                                                },
                                              }));
                                            }
                                          } finally {
                                            setLoadingButtonId(null);
                                          }
                                        }}
                                      >
                                        {t(
                                          'adminSubscription.cancelSubscription'
                                        )}
                                      </button>
                                    )}
                                  </>
                                )}

                                {subStatus.cancel?.type === 'loading' && (
                                  <div className="subscription-message loading">
                                    {subStatus.cancel.text}
                                  </div>
                                )}
                                {subStatus.cancel?.type === 'success' && (
                                  <div className="subscription-message success">
                                    {subStatus.cancel.text}
                                  </div>
                                )}
                                {subStatus.cancel?.type === 'error' && (
                                  <div className="subscription-message error">
                                    {subStatus.cancel.text}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        ) : sub.id === 4 ? (
                          // Elite plan -> mailto
                          <button
                            className="subscribe-button"
                            onClick={() =>
                            (window.location.href =
                              'mailto:contact@dnzify.com?subject=DNZIFY%Custom%20Subscription%20Inquiry')
                            }
                          >
                            {t('adminSubscription.inquire')}
                          </button>
                        ) : (
                          // Other plans -> subscribe button (with confirm modal for existing subs)
                          <div
                            title={
                              !isSchoolAdmin
                                ? t('adminSubscription.accessibleOnlyToPrincipal')
                                : ''
                            }
                            style={{ display: 'inline-block' }}
                          >
                            <button
                              className="subscribe-button"
                              disabled={!isSchoolAdmin || loadingButtonId !== null}
                              onClick={async () => {
                                if (!isSchoolAdmin) return;

                                //prevents false overLimit banner
                                if (sub.id === school.subscription_id) {
                                  // console.log("Already on this plan → skipping eligibility check.");
                                  return;
                                }


                                // 🔥 Always validate first, even if this is the FIRST subscription
                                try {
                                  const checkRes = await axiosPrivate.post('/school/subscribe/check', {
                                    subscription_id: sub.id,
                                  });

                                  // If check passes: ok to continue
                                } catch (err) {
                                  if (err?.response?.status === 400) {
                                    const details = err.response?.data?.details || [];

                                    // Show over-limit error in banner
                                    showBanner(
                                      'error',
                                      `${t('adminSubscription.overLimitError')} ${details.join(', ')}`
                                    );
                                    return; // ⛔ STOP — do not open modal and do not go to Stripe
                                  }

                                  console.error('Eligibility check failed:', err);
                                  showBanner('error', t('adminSubscription.overLimitError'));
                                  return;
                                }

                                // 🔓 Validation OK → continue as before
                                if (school?.stripe_subscription_id) {
                                  // Already subscribed → show confirm modal
                                  setConfirmChange(sub);
                                } else {
                                  // First subscription → go directly to stripe
                                  handleSubscriptionClick(sub);
                                }
                              }}
                            >
                              {loadingButtonId === sub.id ? <LoadingSpinner /> : t('adminSubscription.subscribe')}
                            </button>

                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* 🔹 CUSTOMIZED PLAN (hardcoded) */}
                  <div className="subscription-card customized-subscription">
                    <h3>Customized</h3>

                    <p className="subscription-purpose">
                      Your custom plan
                    </p>

                    {/* PRICE */}
                    <div className="subscription-price">
                      <span className="customized-value"></span>
                    </div>

                    <ul className="subscription-details">
                      <li>
                        <strong>{t('adminSubscription.levels')}</strong>{' '}
                        <span className="customized-value">Customized</span>
                      </li>

                      <li>
                        <strong>{t('adminSubscription.users')}</strong>{' '}
                        <span className="customized-value">Customized</span>
                      </li>

                      <li>
                        <strong>{t('adminSubscription.storage')}</strong>{' '}
                        <span className="customized-value">Customized</span>
                      </li>

                    </ul>

                    <button
                      className="subscribe-button"
                      onClick={() => {
                        window.location.href =
                          'mailto:contact@dnzify.com?subject=DNZIFY%20Custom%20Subscription%20Inquiry';
                      }}
                    >
                      Enquire
                    </button>
                  </div>


                </div>
              )}
            </div>
          }

          {/* Confirm change modal */}
          {confirmChange && (
            <div className="confirm-overlay">
              <div className="modal-body-wrapper">

                <div className="confirm-modal confirm-modal-large">

                  <h3>{t('adminSubscription.confirmChangeTitle')}</h3>
                  <p style={{ fontSize: '15px' }}>{t('adminSubscription.confirmChangeMessage')}</p>

                  {/* PLAN COMPARISON */}
                  <div className="compare-plans-wrapper">
                    {/* CURRENT PLAN */}
                    <div className="compare-plan-card">
                      <h4>{t('adminSubscription.currentSubscription')}</h4>
                      <div className="plan-card-inner">
                        <h5>
                          {t(
                            `adminSubscription.subscription.${getSubscriptionKey(
                              adminSubscription?.subscription_name
                            )}.name`
                          )}
                        </h5>
                        {/* <p className="comparison-price">
                        {currentHasOverride ? (
                          <>
                            CAD ${adminSubscriptionOverrides.price}{' '}
                            <span className="overridden-value">
                              <s>CAD ${adminSubscription.price}</s>
                            </span>
                          </>
                        ) : (
                          <>CAD ${adminSubscription.price}</>
                        )}
                      </p> */}


                        <ul className="comparison-details">

                          {/* PRICE */}
                          <li>
                            {/* <strong>{t('adminSubscription.price')} </strong> */}

                            {currentHasOverride && isDifferent(adminSubscription.price, currentPlanOverride.price) ? (
                              <>
                                CAD ${currentPlanOverride.price}{' '}
                                <span className="overridden-value">
                                  <s>CAD ${adminSubscription.price}</s>
                                </span>

                                {/* CUSTOM badge */}
                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-current-price']: !prev['modal-current-price'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-current-price'] && (
                                  <div className="custom-info-box">{t('adminSubscription.customInfoMessage')}</div>
                                )}
                              </>
                            ) : (
                              <>CAD ${adminSubscription.price}</>
                            )}
                          </li>


                          {/* LEVELS */}
                          <li>
                            <strong>{t('adminSubscription.levels')} </strong>

                            {currentHasOverride && isDifferent(adminSubscription.number_of_levels, currentPlanOverride?.number_of_levels) ? (
                              <>
                                {usedLevels} / {currentPlanOverride.number_of_levels}{' '}
                                <span className="overridden-value">
                                  <s>{adminSubscription.number_of_levels}</s>
                                </span>


                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-current-levels']: !prev['modal-current-levels'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-current-levels'] && (
                                  <div className="custom-info-box">{t('adminSubscription.customInfoMessage')}</div>
                                )}
                              </>
                            ) : (
                              <>
                                {usedLevels} / {adminSubscription.number_of_levels}
                              </>
                            )}
                          </li>



                          {/* USERS */}
                          <li>
                            <strong>{t('adminSubscription.users')} </strong>

                            {currentHasOverride && isDifferent(adminSubscription.number_of_users, currentPlanOverride?.number_of_users) ? (
                              <>
                                {registeredUsers} / {currentPlanOverride.number_of_users}{' '}
                                <span className="overridden-value">
                                  <s>{adminSubscription.number_of_users}</s>
                                </span>


                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-current-users']: !prev['modal-current-users'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-current-users'] && (
                                  <div className="custom-info-box">{t('adminSubscription.customInfoMessage')}</div>
                                )}
                              </>
                            ) : (
                              <>
                                {registeredUsers} / {adminSubscription.number_of_users}
                              </>
                            )}
                          </li>



                          {/* STORAGE */}
                          <li>
                            <strong>{t('adminSubscription.storage')} </strong>

                            {currentHasOverride && isDifferent(adminSubscription.storage_mb, currentPlanOverride?.storage_mb) ? (
                              <>
                                {formatStorageSize(currentPlanOverride.storage_mb)}{' '}
                                <span className="overridden-value">
                                  <s>{formatStorageSize(adminSubscription.storage_mb)}</s>
                                </span>

                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-current-storage']: !prev['modal-current-storage'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-current-storage'] && (
                                  <div className="custom-info-box">{t('adminSubscription.customInfoMessage')}</div>
                                )}
                              </>
                            ) : (
                              formatStorageSize(adminSubscription.storage_mb)
                            )}
                          </li>

                        </ul>


                      </div>
                    </div>

                    {/* NEW PLAN (confirmChange) */}
                    <div className="compare-plan-card">
                      <h4>{t('adminSubscription.newSubscription')}</h4>
                      <div className="plan-card-inner">
                        <h5>
                          {t(
                            `adminSubscription.subscription.${getSubscriptionKey(
                              confirmChange?.subscription_name
                            )}.name`
                          )}
                        </h5>
                        {/* <p className="comparison-price">
                        {newHasOverride ? (
                          <>
                            CAD ${adminSubscriptionOverrides.price}{' '}
                            <span className="overridden-value">
                              <s>CAD ${confirmChange.price}</s>
                            </span>
                          </>
                        ) : (
                          <>CAD ${confirmChange.price}</>
                        )}
                      </p> */}



                        <ul className="comparison-details">

                          {/* PRICE */}
                          <li>


                            {newHasOverride && isCustomValue(confirmChange.price, newPlanOverride?.price) ? (
                              <>
                                CAD ${newPlanOverride.price}{' '}
                                <span className="overridden-value">
                                  <s>CAD ${confirmChange.price}</s>
                                </span>

                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-new-price']: !prev['modal-new-price'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-new-price'] && (
                                  <div className="custom-info-box">
                                    {t('adminSubscription.customInfoMessage')}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>CAD ${confirmChange.price}</>
                            )}
                          </li>



                          {/* LEVELS */}
                          <li>
                            <strong>{t('adminSubscription.levels')} </strong>

                            {newHasOverride && isCustomValue(confirmChange.number_of_levels, newPlanOverride?.number_of_levels) ? (
                              <>
                                {newPlanOverride.number_of_levels}{' '}
                                <span className="overridden-value">
                                  <s>{confirmChange.number_of_levels}</s>
                                </span>


                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-new-levels']: !prev['modal-new-levels'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-new-levels'] && (
                                  <div className="custom-info-box">{t('adminSubscription.customInfoMessage')}</div>
                                )}
                              </>
                            ) : (
                              <>{confirmChange.number_of_levels}</>
                            )}
                          </li>



                          {/* USERS */}
                          <li>
                            <strong>{t('adminSubscription.users')} </strong>

                            {newHasOverride && isCustomValue(confirmChange.number_of_users, newPlanOverride?.number_of_users) ? (
                              <>
                                {newPlanOverride.number_of_users}{' '}
                                <span className="overridden-value">
                                  <s>{confirmChange.number_of_users}</s>
                                </span>


                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-new-users']: !prev['modal-new-users'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-new-users'] && (
                                  <div className="custom-info-box">{t('adminSubscription.customInfoMessage')}</div>
                                )}
                              </>
                            ) : (
                              <>{confirmChange.number_of_users}</>
                            )}
                          </li>



                          {/* STORAGE */}
                          <li>
                            <strong>{t('adminSubscription.storage')} </strong>

                            {newHasOverride && isCustomValue(confirmChange.storage_mb, newPlanOverride?.storage_mb) ? (
                              <>
                                {formatStorageSize(newPlanOverride.storage_mb)}{' '}
                                <span className="overridden-value">
                                  <s>{formatStorageSize(confirmChange.storage_mb)}</s>
                                </span>


                                <span
                                  className="custom-badge"
                                  onClick={() =>
                                    setCustomInfoOpen(prev => ({ ...prev, ['modal-new-storage']: !prev['modal-new-storage'] }))
                                  }
                                >
                                  {t('adminSubscription.customLabel')}
                                </span>

                                {customInfoOpen['modal-new-storage'] && (
                                  <div className="custom-info-box">{t('adminSubscription.customInfoMessage')}</div>
                                )}
                              </>
                            ) : (
                              <>{formatStorageSize(confirmChange.storage_mb)}</>
                            )}

                          </li>

                        </ul>


                      </div>
                    </div>
                  </div>

                  {/* BUTTONS */}
                  <div className="confirm-buttons">
                    <button
                      className="button-red"
                      disabled={loadingButtonId === 'confirmChange'}
                      onClick={() => {
                        if (loadingButtonId !== 'confirmChange') {
                          setConfirmChange(null);
                        }
                      }}
                    >
                      {t('common.cancel')}
                    </button>

                    {loadingButtonId === 'confirmChange' ? (
                      <LoadingSpinner />
                    ) : (
                      <button
                        className="button-white"
                        disabled={!isSchoolAdmin}
                        onClick={async () => {
                          if (!isSchoolAdmin) return;

                          try {
                            const checkRes = await axiosPrivate.post('/school/subscribe/check', {
                              subscription_id: confirmChange.id
                            });
                          } catch (err) {
                            if (err?.response?.status === 400) {
                              let detailsText = '';

                              if (Array.isArray(err.response.data.details)) {
                                detailsText = err.response.data.details.join(', ');
                              }

                              showBanner('error', `${t('adminSubscription.overLimitError')} ${detailsText}`);
                              setConfirmChange(null);
                              return;
                            }
                          }

                          setLoadingButtonId('confirmChange');
                          try {
                            await handleSubscriptionClick(confirmChange, { suppressLoading: true });
                          } catch (err) {
                            console.error('Subscription change failed:', err);
                            showBanner('error', t('adminSubscription.overLimitError'));
                          } finally {
                            setLoadingButtonId(null);
                            setConfirmChange(null);
                          }
                        }}
                      >
                        {t('common.confirm')}
                      </button>
                    )}
                  </div>

                </div>
              </div>
            </div>
          )}

        </div>
      ) : (
        <Error />
      )}
    </div>
  );
}
